/**
 * Paso 0 de la activación (005 · QA-6): verificar los últimos 4 dígitos del documento.
 *
 * Va **antes** de la bienvenida: hasta verificar no se muestra ningún dato del trabajador (ni su
 * nombre), para que un enlace reenviado no sirva a otra persona ("márcame tú"). El servidor compara
 * con `workers.document_number` y, tras 5 intentos fallidos, revoca la invitación: entonces esta
 * pantalla lo explica y solo queda pedir un enlace nuevo al administrador.
 *
 * Solo se muestra la empresa (dato de la invitación, no del trabajador). Los dígitos escritos viven
 * en el estado del componente y en el cuerpo del POST; nunca en `sessionStorage` ni en logs.
 */
import { IdCard, ShieldX } from 'lucide-react';
import { useState } from 'react';

import { DOCUMENT_LAST_CHARS, type ActivationInfo } from '@fermin/contracts';

import { Button } from '../../components/ui/button';
import { CodeInput } from '../../components/ui/code-input';
import { Screen } from '../../components/ui/screen';
import { t } from '../../i18n';

import { attemptsRemaining, useVerifyDocument } from './api';
import { isInvitationGone } from './link-invalid';

/** Letras y dígitos: el DNI es numérico, pero el carné de extranjería y el pasaporte no siempre. */
export const DOCUMENT_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/**
 * Solo dígitos (piloto 2026-09-30): casi todos los documentos terminan en números y el teclado de
 * letras hacía que un carácter mal tecleado se enviara solo y gastara un intento. Quien tenga letras
 * al final lo pide con "Mi documento termina en letras" (teclado completo y envío solo con el botón).
 */
export const DOCUMENT_DIGITS = '0123456789';

export interface VerifyDocumentStepProps {
  token: string;
  /** Empresa que invita (de `GET /activation/{token}`); sin datos del trabajador. */
  companyTradeName: string;
  /** `200`: el `ActivationInfo` ya trae el nombre de pila. */
  onVerified: (info: ActivationInfo) => void;
  /** `410`: el enlace ya no sirve. */
  onGone: () => void;
}

/** Texto del error en línea para un intento fallido (o `null` si no hay error que mostrar). */
export function verifyErrorMessage(
  error: Parameters<typeof attemptsRemaining>[0],
): string | null {
  if (error === null || isInvitationGone(error)) {
    return null;
  }
  const remaining = attemptsRemaining(error);
  if (remaining !== null) {
    return remaining === 1
      ? t('activation.verify.mismatchOne')
      : t('activation.verify.mismatch', { count: remaining });
  }
  if (error.is('RATE_LIMITED')) {
    return t('activation.verify.rateLimited');
  }
  return error.message || t('activation.verify.error');
}

/** Pantalla final cuando se agotaron los intentos: la invitación quedó revocada en el servidor. */
export function VerificationLockedScreen() {
  return (
    <Screen
      data-testid="activation-verify-locked"
      align="center"
      title={t('activation.verify.lockedTitle')}
      lead={t('activation.verify.lockedDescription')}
    >
      <div className="flex justify-center text-fm-danger-text">
        <ShieldX aria-hidden="true" size={40} />
      </div>
    </Screen>
  );
}

export function VerifyDocumentStep({
  token,
  companyTradeName,
  onVerified,
  onGone,
}: VerifyDocumentStepProps) {
  const [digits, setDigits] = useState('');
  const [withLetters, setWithLetters] = useState(false);
  const verify = useVerifyDocument(token);
  const complete = digits.length === DOCUMENT_LAST_CHARS;

  function submit(value: string): void {
    if (value.length !== DOCUMENT_LAST_CHARS || verify.isPending) {
      return;
    }
    verify.mutate(value, {
      onSuccess: onVerified,
      onError: (error) => {
        if (isInvitationGone(error)) {
          onGone();
          return;
        }
        // Intento fallido: se vacían las celdas para escribir de nuevo.
        setDigits('');
      },
    });
  }

  if (attemptsRemaining(verify.error) === 0) {
    return <VerificationLockedScreen />;
  }

  const errorMessage = verifyErrorMessage(verify.error);

  return (
    <Screen
      data-testid="activation-verify"
      title={t('activation.verify.title')}
      lead={t('activation.verify.lead', { company: companyTradeName })}
      footer={
        <Button
          disabled={!complete}
          loading={verify.isPending}
          loadingText={t('common.states.verifying')}
          icon={<IdCard aria-hidden="true" size={20} />}
          onClick={() => submit(digits)}
        >
          {t('activation.verify.action')}
        </Button>
      }
    >
      <CodeInput
        value={digits}
        onChange={(next) => {
          if (verify.isError) {
            verify.reset();
          }
          setDigits(next);
        }}
        // Con letras no se envía solo: un carácter equivocado gastaría un intento.
        {...(withLetters ? {} : { onComplete: submit })}
        length={DOCUMENT_LAST_CHARS}
        alphabet={withLetters ? DOCUMENT_ALPHABET : DOCUMENT_DIGITS}
        keyboard={withLetters ? 'text' : 'numeric'}
        label={t('activation.verify.label')}
        invalid={errorMessage !== null}
        disabled={verify.isPending}
        autoFocus
        describedBy="verify-document-help"
      />
      <p
        id="verify-document-help"
        data-testid="verify-document-help"
        role={errorMessage !== null ? 'alert' : undefined}
        className={
          errorMessage !== null
            ? 'text-fm-sm text-fm-danger-text'
            : 'text-fm-sm text-fm-text-secondary'
        }
      >
        {errorMessage ?? t('activation.verify.help')}
      </p>
      <button
        type="button"
        data-testid="verify-document-letters"
        className="min-h-11 self-start text-fm-sm font-semibold text-fm-primary-text underline underline-offset-2"
        onClick={() => {
          setWithLetters((current) => !current);
          setDigits('');
        }}
      >
        {withLetters ? t('activation.verify.digitsOnly') : t('activation.verify.hasLetters')}
      </button>
    </Screen>
  );
}
